import { useEffect, useState, useCallback, useRef } from 'react';

export const useGoogleIdentity = ({ onSuccess, onError }) => {
    const [codeClient, setCodeClient] = useState(null);
    const [gisLoaded, setGisLoaded] = useState(false);
    const onSuccessRef = useRef(onSuccess);
    const onErrorRef = useRef(onError);

    // Update refs when callbacks change
    useEffect(() => {
        onSuccessRef.current = onSuccess;
        onErrorRef.current = onError;
    }, [onSuccess, onError]);

    // Check for GIS script and wait for it to load
    useEffect(() => {
        const checkGisLoaded = () => {
            if (window.google && window.google.accounts) {
                setGisLoaded(true);
                return true;
            }
            return false;
        };

        // Check immediately
        if (checkGisLoaded()) {
            return;
        }

        // If not loaded, wait for script to load
        const scriptCheckInterval = setInterval(() => {
            if (checkGisLoaded()) {
                clearInterval(scriptCheckInterval);
            }
        }, 100);

        // Cleanup interval on unmount
        return () => clearInterval(scriptCheckInterval);
    }, []);

    // Initialize code client once GIS is loaded
    useEffect(() => {
        if (!gisLoaded) {
            return;
        }

        const client = window.google.accounts.oauth2.initCodeClient({
            client_id: process.env.REACT_APP_GOOGLE_CLIENT_ID, // Your web client ID
            scope: 'https://www.googleapis.com/auth/drive.file',
            callback: (response) => {
                if (response.code) {
                    onSuccessRef.current(response.code);
                } else {
                    onErrorRef.current(response);
                }
            },
            error_callback: (error) => {
                console.error('Google Auth Code Error:', error);
                onErrorRef.current(error);
            }
        });
        setCodeClient(client);
    }, [gisLoaded]);

    const requestAuthCode = useCallback(() => {
        if (codeClient) {
            // This triggers the Google popup
            codeClient.requestCode();
        } else {
            console.warn('Google Identity Services not ready yet');
        }
    }, [codeClient]);

    return { requestAuthCode, gisLoaded };
};